import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import {
  DIGO_CLIENT_USER_TYPE,
  DIGO_WORKER_USER_TYPE,
  signupLinkProps,
} from '../LandingPage/digoServices';

import {
  HANDYMAN_HUB,
  HANDYMAN_STEPS,
  HANDYMAN_REGIONS,
  findHandymanRegion,
} from './handymanContent';
import css from './HandymanPage.module.css';

// Pages SEO « Homme à tout faire » : une page mère (/homme-a-tout-faire) et une page par
// région (/homme-a-tout-faire/:region). Elles ne sont liées depuis le site que par le lien
// « Régions desservies » du footer; elles se lient entre elles. Le contenu vit dans
// handymanContent.js.

const ROOT_URL = 'https://digo.ca';
const HUB_PATH = '/homme-a-tout-faire';

const faqSchema = faq => ({
  '@type': 'FAQPage',
  mainEntity: faq.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

const breadcrumbSchema = region => {
  const items = [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${ROOT_URL}/` },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Homme à tout faire',
      item: `${ROOT_URL}${HUB_PATH}`,
    },
  ];
  if (region) {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: region.name,
      item: `${ROOT_URL}${HUB_PATH}/${region.slug}`,
    });
  }
  return { '@type': 'BreadcrumbList', itemListElement: items };
};

const serviceSchema = region => ({
  '@type': 'Service',
  name: region ? region.h1 : 'Homme à tout faire (Humains à tout faire)',
  serviceType: 'Homme à tout faire',
  url: region ? `${ROOT_URL}${HUB_PATH}/${region.slug}` : `${ROOT_URL}${HUB_PATH}`,
  areaServed: region
    ? { '@type': region.areaType, name: region.areaName }
    : { '@type': 'AdministrativeArea', name: 'Québec' },
  provider: { '@type': 'Organization', name: 'DIGO', url: ROOT_URL },
  description:
    "Plateforme québécoise qui met en contact les clients avec des travailleurs autonomes pour le montage de meubles, le déneigement, le rangement, l'entretien de terrain et d'autres tâches du quotidien.",
});

const Ctas = () => (
  <div className={css.ctas}>
    <NamedLink {...signupLinkProps(DIGO_CLIENT_USER_TYPE)} className={css.ctaPrimary}>
      Publier une demande
    </NamedLink>
    <NamedLink {...signupLinkProps(DIGO_WORKER_USER_TYPE)} className={css.ctaSecondary}>
      Devenir travailleur
    </NamedLink>
  </div>
);

const Steps = () => (
  <section className={css.section}>
    <div className={css.inner}>
      <h2 className={css.sectionTitle}>Comment ça fonctionne</h2>
      <ol className={css.steps}>
        {HANDYMAN_STEPS.map((step, i) => (
          <li key={step.title} className={css.step}>
            <span className={css.stepNumber}>{i + 1}</span>
            <div>
              <h3 className={css.stepTitle}>{step.title}</h3>
              <p className={css.text}>{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Faq = ({ title, faq }) => (
  <section className={css.section}>
    <div className={css.inner}>
      <h2 className={css.sectionTitle}>{title}</h2>
      <div className={css.faq}>
        {faq.map(item => (
          <div key={item.question} className={css.faqItem}>
            <h3 className={css.faqQuestion}>{item.question}</h3>
            <p className={css.text}>{item.answer}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const RegionLinks = ({ title, intro, currentSlug }) => (
  <section className={css.sectionAlt}>
    <div className={css.inner}>
      <h2 className={css.sectionTitle}>{title}</h2>
      {intro ? <p className={css.text}>{intro}</p> : null}
      <ul className={css.regionList}>
        {HANDYMAN_REGIONS.filter(r => r.slug !== currentSlug).map(r => (
          <li key={r.slug}>
            <NamedLink
              name="HandymanRegionPage"
              params={{ region: r.slug }}
              className={css.regionLink}
            >
              {r.linkLabel}
            </NamedLink>
          </li>
        ))}
        {currentSlug ? (
          <li>
            <NamedLink name="HandymanPage" className={css.regionLink}>
              Homme à tout faire : toutes les régions
            </NamedLink>
          </li>
        ) : null}
      </ul>
    </div>
  </section>
);

const Breadcrumb = ({ region }) => (
  <nav className={css.breadcrumb} aria-label="Fil d'Ariane">
    <NamedLink name="LandingPage">Accueil</NamedLink>
    <span aria-hidden="true"> / </span>
    {region ? (
      <>
        <NamedLink name="HandymanPage">Homme à tout faire</NamedLink>
        <span aria-hidden="true"> / </span>
        <span>{region.name}</span>
      </>
    ) : (
      <span>Homme à tout faire</span>
    )}
  </nav>
);

const HubContent = () => {
  const hub = HANDYMAN_HUB;
  return (
    <>
      <section className={css.hero}>
        <div className={css.heroInner}>
          <Breadcrumb />
          <h1 className={css.heroTitle}>{hub.h1}</h1>
          {hub.intro.map(p => (
            <p key={p} className={css.heroLead}>
              {p}
            </p>
          ))}
          <Ctas />
        </div>
      </section>
      <section className={css.section}>
        <div className={css.inner}>
          <h2 className={css.sectionTitle}>{hub.whyTitle}</h2>
          {hub.why.map(p => (
            <p key={p} className={css.text}>
              {p}
            </p>
          ))}
        </div>
      </section>
      <section className={css.sectionAlt}>
        <div className={css.inner}>
          <h2 className={css.sectionTitle}>{hub.servicesTitle}</h2>
          <ul className={css.chips}>
            {hub.services.map(s => (
              <li key={s} className={css.chip}>
                {s}
              </li>
            ))}
          </ul>
          <p className={css.text}>{hub.servicesNote}</p>
        </div>
      </section>
      <section className={css.section}>
        <div className={css.inner}>
          <h2 className={css.sectionTitle}>{hub.excludedTitle}</h2>
          <p className={css.text}>{hub.excluded}</p>
        </div>
      </section>
      <Steps />
      <section className={css.sectionDark}>
        <div className={css.inner}>
          <h2 className={css.sectionTitleLight}>{hub.workerTitle}</h2>
          <p className={css.textLight}>{hub.worker}</p>
          <Ctas />
        </div>
      </section>
      <RegionLinks title={hub.regionsTitle} intro={hub.regionsIntro} />
      <Faq title="Questions fréquentes" faq={hub.faq} />
    </>
  );
};

const RegionContent = ({ region }) => (
  <>
    <section className={css.hero}>
      <div className={css.heroInner}>
        <Breadcrumb region={region} />
        <h1 className={css.heroTitle}>{region.h1}</h1>
        <p className={css.heroLead}>{region.intro}</p>
        <Ctas />
      </div>
    </section>
    <section className={css.section}>
      <div className={css.inner}>
        <h2 className={css.sectionTitle}>{region.servicesTitle}</h2>
        <ul className={css.serviceList}>
          {region.services.map(s => (
            <li key={s.label} className={css.serviceItem}>
              <strong>{s.label}</strong> : {s.text}
            </li>
          ))}
        </ul>
      </div>
    </section>
    <section className={css.sectionAlt}>
      <div className={css.inner}>
        <h2 className={css.sectionTitle}>{region.localTitle}</h2>
        <p className={css.text}>{region.local}</p>
      </div>
    </section>
    <section className={css.section}>
      <div className={css.inner}>
        <h2 className={css.sectionTitle}>{region.sectorsTitle}</h2>
        <p className={css.text}>{region.sectors}</p>
      </div>
    </section>
    <Steps />
    <section className={css.sectionDark}>
      <div className={css.inner}>
        <h2 className={css.sectionTitleLight}>{region.workerTitle}</h2>
        <p className={css.textLight}>{region.worker}</p>
        <Ctas />
      </div>
    </section>
    <Faq title={region.faqTitle} faq={region.faq} />
    <RegionLinks title="Ailleurs au Québec" currentSlug={region.slug} />
  </>
);

export const HandymanPageComponent = props => {
  const { scrollingDisabled, params = {}, staticContext } = props;
  const regionSlug = params.region;
  const region = regionSlug ? findHandymanRegion(regionSlug) : null;

  if (regionSlug && !region) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const title = region ? region.title : HANDYMAN_HUB.title;
  const description = region ? region.description : HANDYMAN_HUB.description;
  const faq = region ? region.faq : HANDYMAN_HUB.faq;
  const schema = [serviceSchema(region), faqSchema(faq), breadcrumbSchema(region)];

  return (
    <Page
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {region ? <RegionContent region={region} /> : <HubContent />}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HandymanPage = connect(mapStateToProps)(HandymanPageComponent);

export default HandymanPage;
